import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import commitsData from '../data/commits.json';
import { usePageTitle } from '../hooks/shared';

// Shared hover state — tracks which commit hash is currently expanded.
// Only one bubble is active at a time; switching commits collapses the previous one.
const BubbleHoverCtx = createContext({ activeHash: null, setActiveHash: () => {} });

const display = { fontFamily: "'Permanent Marker', cursive" };
const body = { fontFamily: "'Patrick Hand', cursive" };
const mono = { fontFamily: "'Special Elite', monospace" };

// Slugifies an arc label into a stable DOM id.
function arcId(label) {
  return `arc-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

// Passive scroll listener that returns the id of whichever arc heading is
// currently at or above the 35%-from-top threshold of the viewport.
function useScrollSpy(ids) {
  const [activeId, setActiveId] = useState(ids[0] ?? null);

  useEffect(() => {
    if (ids.length === 0) return;

    function update() {
      const threshold = window.scrollY + window.innerHeight * 0.35;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= threshold) {
          current = id;
        }
      }
      setActiveId(current);
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [ids]);

  return activeId;
}

const PAGE_STYLES = `
  /* ── Scroll-spy dot nav ───────────────────────────────────────── */
  .dot-nav-btn {
    background: none;
    border: none;
    padding: 0;
    width: 28px;
    height: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .dot-nav-indicator {
    display: block;
    flex-shrink: 0;
  }
  .dot-nav-label {
    position: absolute;
    left: 1.8rem;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
    font-size: 0.6rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #1a1a1a;
    background: #fefcf6;
    border: 1.5px solid #1a1a1a;
    padding: 2px 7px;
    box-shadow: 2px 2px 0 #1a1a1a;
    opacity: 0;
    pointer-events: none;
  }
  .dot-nav-btn:hover .dot-nav-label,
  .dot-nav-btn:focus-visible .dot-nav-label {
    opacity: 1;
  }
  @media (prefers-reduced-motion: no-preference) {
    .dot-nav-indicator line {
      transition:
        stroke 200ms ease,
        stroke-width 250ms cubic-bezier(0, 1.39, 0.88, 0.94);
    }
    .dot-nav-label {
      transition: opacity 140ms ease;
    }
  }

  /* ── Commit speech bubbles ────────────────────────────────────── */
  .commit-bubble-wrapper {
    max-width: 8rem;
  }
  @media (prefers-reduced-motion: no-preference) {
    .commit-bubble-wrapper {
      transition: max-width 380ms cubic-bezier(0, 1.39, 0.88, 0.94);
    }
  }
  /* CSS :hover handles scroll-past reactivity; .bubble-hovered locks expansion via JS */
  .timeline-free .commit-bubble-wrapper:hover,
  .bubble-hovered {
    max-width: 17rem;
  }
  /* Collapsed summary clamped to 2 lines */
  .bubble-summary {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    opacity: 1;
  }
  .timeline-free .commit-bubble-wrapper:hover .bubble-summary,
  .bubble-hovered .bubble-summary {
    opacity: 0;
  }
  /* Full subject: first item inside expanded area, fades in with the grid */
  .bubble-full-subject {
    opacity: 0;
  }
  .timeline-free .commit-bubble-wrapper:hover .bubble-full-subject,
  .bubble-hovered .bubble-full-subject {
    opacity: 1;
  }
  /* Expanded content grid — layout 350ms so neighbors never jump */
  .bubble-expanded-grid {
    display: grid;
    grid-template-rows: 0fr;
  }
  .timeline-free .commit-bubble-wrapper:hover .bubble-expanded-grid,
  .bubble-hovered .bubble-expanded-grid {
    grid-template-rows: 1fr;
  }
  .bubble-expanded {
    overflow: hidden;
    min-height: 0;
  }
  /* Meta row (badge + date): own grid cell that grows from 0 within expanded area */
  .bubble-meta-grid {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
  }
  .timeline-free .commit-bubble-wrapper:hover .bubble-meta-grid,
  .bubble-hovered .bubble-meta-grid {
    grid-template-rows: 1fr;
  }
  .bubble-meta {
    overflow: hidden;
    min-height: 0;
  }
  .timeline-free .commit-bubble-wrapper:hover .bubble-meta,
  .bubble-hovered .bubble-meta {
    padding-bottom: 0.25rem;
  }
  @media (prefers-reduced-motion: no-preference) {
    .bubble-expanded-grid,
    .timeline-free .commit-bubble-wrapper:hover .bubble-expanded-grid,
    .bubble-hovered .bubble-expanded-grid {
      transition: grid-template-rows 350ms ease-in-out;
    }
    .bubble-summary {
      transition: opacity 350ms ease-in-out;
    }
    .timeline-free .commit-bubble-wrapper:hover .bubble-summary,
    .bubble-hovered .bubble-summary {
      transition: opacity 130ms ease-in;
    }
    .bubble-meta-grid,
    .timeline-free .commit-bubble-wrapper:hover .bubble-meta-grid,
    .bubble-hovered .bubble-meta-grid {
      transition: grid-template-rows 250ms ease-in-out;
    }
    .bubble-full-subject {
      transition: opacity 130ms ease-in;
    }
    .timeline-free .commit-bubble-wrapper:hover .bubble-full-subject,
    .bubble-hovered .bubble-full-subject {
      transition: opacity 350ms ease-in-out;
    }
  }
  .commit-bubble {
    padding: 0.3rem 0.6rem;
    background: #fefcf6;
    border: 2px solid #1a1a1a;
    box-shadow: 2px 2px 0 #1a1a1a;
    position: relative;
    min-width: 4rem;
  }
  /* Arrow pointing left — right-side bubble toward center */
  .bubble-right::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 50%;
    margin-top: -7px;
    border: 7px solid transparent;
    border-right: 8px solid #1a1a1a;
    border-left: none;
  }
  .bubble-right::after {
    content: '';
    position: absolute;
    left: -5px;
    top: 50%;
    margin-top: -6px;
    border: 6px solid transparent;
    border-right: 7px solid #fefcf6;
    border-left: none;
  }
  /* Arrow pointing right — left-side bubble toward center */
  .bubble-left::before {
    content: '';
    position: absolute;
    right: -8px;
    top: 50%;
    margin-top: -7px;
    border: 7px solid transparent;
    border-left: 8px solid #1a1a1a;
    border-right: none;
  }
  .bubble-left::after {
    content: '';
    position: absolute;
    right: -5px;
    top: 50%;
    margin-top: -6px;
    border: 6px solid transparent;
    border-left: 7px solid #fefcf6;
    border-right: none;
  }

/* ── Ink dot tooltip ──────────────────────────────────────────── */
  .ink-dot-tip {
    position: relative;
    cursor: default;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .ink-dot-tip::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: #1a1a1a;
    color: #fefcf6;
    font-size: 9px;
    padding: 2px 7px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    z-index: 50;
    letter-spacing: 0.08em;
    font-family: 'Special Elite', monospace;
  }
  .ink-dot-tip:hover::after {
    opacity: 1;
  }
  @media (prefers-reduced-motion: no-preference) {
    .ink-dot-tip::after {
      transition: opacity 120ms ease;
    }
  }
`;

const TYPE_CONFIG = {
  feat: {
    label: 'FEATURE',
    color: '#2a7d4f',
    bg: '#e8f5ed',
    description: 'New capability added to the app',
  },
  fix: {
    label: 'BUG FIX',
    color: '#d62828',
    bg: '#fdf0f0',
    description: 'Something broken was repaired',
  },
  refactor: {
    label: 'REFACTOR',
    color: '#1a5fad',
    bg: '#eef4fd',
    description: 'Code restructured without changing behaviour',
  },
  improvement: {
    label: 'META',
    color: '#e07a00',
    bg: '#fdf4e8',
    description: 'Trying a new way of working with AI',
  },
  removal: {
    label: 'CLEANUP',
    color: '#6b5f4d',
    bg: '#f5ede0',
    description: 'Dead code, legacy patterns, or clutter removed',
  },
  docs: {
    label: 'DOCS',
    color: '#7c3aed',
    bg: '#f5f0ff',
    description: 'Documentation or comments updated',
  },
  other: {
    label: 'OTHER',
    color: '#1a1a1a',
    bg: '#ede5d4',
    description: 'Miscellaneous or uncategorised change',
  },
};

const ARC_PALETTE = [
  '#ffd166', // amber
  '#ef6c9f', // pink
  '#43d9a2', // mint
  '#56b4e9', // sky
  '#f4a261', // peach
  '#b48eed', // lavender
  '#e76f51', // coral
  '#52b788', // sage
  '#ffb703', // gold
  '#a8d5ba', // seafoam
];

function groupByArc(commits) {
  const groups = [];
  for (const commit of commits) {
    const arcKey = commit.arc?.label ?? null;
    const last = groups[groups.length - 1];
    if (last && last.arcKey === arcKey) {
      last.commits.push(commit);
    } else {
      groups.push({ arcKey, arc: commit.arc, commits: [commit] });
    }
  }
  return groups;
}

// ── Scroll-spy dot navigation ──────────────────────────────────────────────

function DotNav({ arcs, activeArcId }) {
  if (arcs.length < 2) return null;

  return (
    <nav className="flex flex-col gap-2 items-center" aria-label="Arc navigation">
      {arcs.map((arc) => {
        const id = arcId(arc.label);
        const isActive = activeArcId === id;
        return (
          <button
            key={id}
            className="dot-nav-btn"
            onClick={() =>
              document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            aria-label={`Jump to ${arc.label}`}
          >
            <svg
              className="dot-nav-indicator"
              width="6"
              height="18"
              viewBox="0 0 6 18"
              fill="none"
              aria-hidden="true"
            >
              <line
                x1="3"
                y1="1"
                x2="3"
                y2="17"
                stroke={isActive ? '#1a1a1a' : '#9b8f7a'}
                strokeWidth={isActive ? 2.5 : 1.5}
                strokeLinecap="round"
                strokeDasharray={
                  isActive ? '2.5 0.8 3.2 0.5 2 1 3 0.6 2.8 0.4' : '1.5 1.2 2 0.8 1.8 1.4 1.2 1'
                }
              />
            </svg>
            <span className="dot-nav-label" style={mono}>
              {arc.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ── Timeline components ────────────────────────────────────────────────────

// Log-scale dot size: 1 line → 8px, 1000 lines → ~30px.
function dotSizePx(totalChanged) {
  if (!totalChanged) return 8;
  return Math.round(Math.min(8 + Math.log1p(totalChanged) * 2.8, 30));
}

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.other;
  return (
    <span
      className="inline-block text-[10px] font-bold tracking-[0.15em] px-2 py-0.5 border border-current"
      style={{ ...mono, color: cfg.color, background: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

function ArcLeaf({ arc, id, color, commitCount }) {
  const accent = color ?? '#fcbf49';
  return (
    <div
      id={id}
      className="w-full mb-3 mt-1 border-2 border-[#1a1a1a] px-4 pt-2.5 pb-2.5"
      style={{
        background: '#ede5d4',
        boxShadow: '3px 3px 0 #1a1a1a',
        transform: 'rotate(-0.3deg)',
        borderLeft: `5px solid ${accent}`,
      }}
    >
      <div
        className="text-[9px] uppercase tracking-[0.25em] mb-0.5"
        style={{ ...mono, color: accent }}
      >
        Arc
      </div>
      <h3 className="text-lg md:text-xl text-[#1a1a1a] leading-tight" style={display}>
        {arc.label}
      </h3>
      {arc.description && (
        <p className="text-xs text-[#6b5f4d] mt-1 leading-relaxed" style={body}>
          {arc.description}
        </p>
      )}
      {commitCount != null && (
        <p className="text-[10px] text-[#6b5f4d] mt-1.5" style={mono}>
          {commitCount} commit{commitCount !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

const CommitBubble = memo(function CommitBubble({ commit }) {
  const { activeHash, setActiveHash } = useContext(BubbleHoverCtx);
  const isExpanded = activeHash === commit.hash;

  const total = commit.totalChanged ?? 0;
  const size = dotSizePx(total);
  const tip = total
    ? `+${commit.additions ?? 0} −${commit.deletions ?? 0}  (${total} lines)`
    : 'no diff data';
  const isRight = parseInt(commit.hash[6] ?? '0', 16) < 8;

  const firstSentence = commit.subject.match(/^[^.!?]+[.!?]/)?.[0] ?? commit.subject;
  const summary = firstSentence.length <= 50 ? firstSentence : `${firstSentence.slice(0, 50)}…`;
  const hasMore = summary !== commit.subject;

  const bodyPreview = commit.body ? commit.body.slice(0, 200).replace(/\s+/g, ' ').trim() : null;
  const bodyTruncated = commit.body && commit.body.length > 200;

  const metaGridRef = useRef(null);
  const expandedGridRef = useRef(null);
  const summaryPRef = useRef(null);
  const spacerRef = useRef(null);
  const spacerRafRef = useRef(null);
  const spacerTimerRef = useRef(null);
  const prevExpandedRef = useRef(false);

  // Drive spacer animation from isExpanded changes rather than mouse events,
  // so the animation plays to completion regardless of cursor movement.
  useEffect(() => {
    const wasExpanded = prevExpandedRef.current;
    prevExpandedRef.current = isExpanded;

    if (!wasExpanded && isExpanded) {
      // Became active — cancel any in-flight spacer.
      cancelAnimationFrame(spacerRafRef.current);
      clearTimeout(spacerTimerRef.current);
      const spacer = spacerRef.current;
      if (spacer) {
        spacer.style.transition = 'none';
        spacer.style.height = '0px';
      }
      return;
    }

    if (wasExpanded && !isExpanded) {
      // Became inactive — grow spacer to hold the gap, then release.
      cancelAnimationFrame(spacerRafRef.current);
      clearTimeout(spacerTimerRef.current);
      const expandedGrid = expandedGridRef.current;
      const spacer = spacerRef.current;
      if (!expandedGrid || !spacer) return;

      const metaH = metaGridRef.current?.offsetHeight ?? 0;
      const expandedH = expandedGrid.offsetHeight;
      const delta = metaH + expandedH;
      if (delta === 0) return;

      spacer.style.transition = 'none';
      spacer.style.height = '0px';

      spacerRafRef.current = requestAnimationFrame(() => {
        spacer.style.transition = 'height 350ms ease-in-out';
        spacer.style.height = `${delta}px`;
        spacerTimerRef.current = setTimeout(() => {
          spacer.style.transition = 'height 450ms ease-in-out';
          spacer.style.height = '0px';
        }, 420);
      });
    }
  }, [isExpanded]);

  const bubble = (
    <div className={`commit-bubble ${isRight ? 'bubble-right' : 'bubble-left'}`}>
      {/* Header: grows from 0 at the top of the bubble */}
      <div ref={metaGridRef} className="bubble-meta-grid">
        <div className="bubble-meta flex items-center gap-1.5 flex-wrap">
          <TypeBadge type={commit.type} />
          <span className="text-[9px] text-[#6b5f4d]" style={mono}>
            {commit.date} · {commit.shortHash}
          </span>
        </div>
      </div>
      <p
        ref={summaryPRef}
        className="bubble-summary text-[11px] text-[#1a1a1a] leading-snug"
        style={body}
      >
        {summary}
        {hasMore && <span className="opacity-40 ml-0.5">…</span>}
      </p>
      {/* Expanded: full subject + detail grow from 0, sizing around actual content */}
      <div ref={expandedGridRef} className="bubble-expanded-grid">
        <div className="bubble-expanded">
          <p className="bubble-full-subject text-[11px] text-[#1a1a1a] leading-snug" style={body}>
            {commit.subject}
          </p>
          {total > 0 && (
            <p className="text-[9px] text-[#6b5f4d] mt-1" style={mono}>
              +{commit.additions} −{commit.deletions}
            </p>
          )}
          {bodyPreview && (
            <p className="text-[10px] text-[#6b5f4d] mt-1.5 leading-relaxed" style={body}>
              {bodyPreview}
              {bodyTruncated && <span className="opacity-40">…</span>}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex items-center mb-1.5">
        <div className="flex-1 flex justify-end pr-3 min-w-0">
          {!isRight && (
            <div
              className={`commit-bubble-wrapper${isExpanded ? ' bubble-hovered' : ''}`}
              onMouseEnter={() => setActiveHash(commit.hash)}
            >
              {bubble}
            </div>
          )}
        </div>
        <div className="w-8 shrink-0 flex justify-center items-center">
          <div
            className="ink-dot-tip"
            data-tip={tip}
            style={{ width: size, height: size, background: '#1a1a1a' }}
          />
        </div>
        <div className="flex-1 flex justify-start pl-3 min-w-0">
          {isRight && (
            <div
              className={`commit-bubble-wrapper${isExpanded ? ' bubble-hovered' : ''}`}
              onMouseEnter={() => setActiveHash(commit.hash)}
            >
              {bubble}
            </div>
          )}
        </div>
      </div>
      <div ref={spacerRef} style={{ height: 0, overflow: 'hidden' }} aria-hidden="true" />
    </>
  );
});

function DateDivider({ date }) {
  const [year, month, day] = date.split('-').map(Number);
  const label = new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return (
    <div className="flex items-center gap-2 my-2">
      <div className="flex-1 border-b border-[#e0d9ce]" />
      <span
        className="text-[9px] uppercase tracking-[0.18em] shrink-0 px-1"
        style={{ ...mono, color: '#bdb0a0' }}
      >
        {label}
      </span>
      <div className="flex-1 border-b border-[#e0d9ce]" />
    </div>
  );
}

function CommitGroup({ group, arcColor }) {
  const id = group.arc ? arcId(group.arc.label) : null;
  const color = arcColor ?? null;

  // Group commits by date to insert day dividers.
  const dateGroups = [];
  for (const commit of group.commits) {
    const last = dateGroups[dateGroups.length - 1];
    if (last && last.date === commit.date) {
      last.commits.push(commit);
    } else {
      dateGroups.push({ date: commit.date, commits: [commit] });
    }
  }

  return (
    <div
      className="mb-4 mt-3"
      style={
        color
          ? { outline: `2.5px solid ${color}`, outlineOffset: '6px', background: `${color}12` }
          : undefined
      }
    >
      {group.arc && (
        <ArcLeaf arc={group.arc} id={id} color={color} commitCount={group.commits.length} />
      )}
      {dateGroups.map((dg) => (
        <div key={dg.date}>
          <DateDivider date={dg.date} />
          {dg.commits.map((commit) => (
            <CommitBubble key={commit.hash} commit={commit} />
          ))}
        </div>
      ))}
    </div>
  );
}

function StatPill({ type, count }) {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.other;
  const [show, setShow] = useState(false);
  const pillRef = useRef(null);
  const tipRef = useRef(null);
  const touchTimer = useRef(null);

  // After the tooltip renders, clamp its position to the viewport.
  useEffect(() => {
    if (!show || !pillRef.current || !tipRef.current) return;
    const pill = pillRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const ideal = pill.left + pill.width / 2 - tip.width / 2;
    const left = Math.max(8, Math.min(ideal, window.innerWidth - tip.width - 8));
    tipRef.current.style.left = `${left}px`;
    tipRef.current.style.top = `${pill.bottom + 6}px`;
  }, [show]);

  // Dismiss on scroll or tap-outside while tooltip is open.
  useEffect(() => {
    if (!show) return;
    function dismiss() {
      setShow(false);
      clearTimeout(touchTimer.current);
    }
    window.addEventListener('scroll', dismiss, { passive: true });
    window.addEventListener('touchstart', dismiss, { passive: true });
    return () => {
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('touchstart', dismiss);
    };
  }, [show]);

  return (
    <div
      ref={pillRef}
      className="flex items-center gap-2 px-3 py-1.5 border-2 border-[#1a1a1a] cursor-default"
      style={{ background: cfg.bg, boxShadow: '2px 2px 0 #1a1a1a' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => {
        clearTimeout(touchTimer.current);
        setShow(false);
      }}
      onClick={() => {
        clearTimeout(touchTimer.current);
        setShow(true);
        touchTimer.current = setTimeout(() => setShow(false), 2000);
      }}
    >
      <span
        className="w-2.5 h-2.5 rounded-full border border-[#1a1a1a]"
        style={{ background: cfg.color }}
      />
      <span className="text-xs font-bold tracking-wide text-[#1a1a1a]" style={mono}>
        {cfg.label}
      </span>
      <span className="text-xs text-[#1a1a1a]" style={mono}>
        {count}
      </span>
      {show &&
        createPortal(
          <div
            ref={tipRef}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              background: '#1a1a1a',
              color: '#fefcf6',
              fontSize: '9px',
              padding: '3px 8px',
              whiteSpace: 'nowrap',
              zIndex: 9999,
              letterSpacing: '0.06em',
              pointerEvents: 'none',
              fontFamily: "'Special Elite', monospace",
            }}
          >
            {cfg.description}
          </div>,
          document.body
        )}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="border-2 border-[#1a1a1a] bg-[#fefcf6] p-8 text-center mx-auto max-w-lg"
      style={{ boxShadow: '4px 4px 0 #1a1a1a' }}
    >
      <p className="text-xl text-[#1a1a1a] mb-2" style={display}>
        NO DATA YET
      </p>
      <p className="text-base text-[#6b5f4d] mb-4" style={body}>
        Run the generation script to populate this page from your git history.
      </p>
      <code className="block text-sm bg-[#1a1a1a] text-[#fefcf6] px-4 py-2" style={mono}>
        npm run gen:timeline
      </code>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function Timeline() {
  usePageTitle('Build Log');

  const { months, totalCommits, generatedAt } = commitsData;

  // Unique arcs in page order (newest first, matching git log order).
  const arcs = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const month of months) {
      for (const commit of month.commits) {
        if (commit.arc && !seen.has(commit.arc.label)) {
          seen.add(commit.arc.label);
          result.push(commit.arc);
        }
      }
    }
    return result;
  }, [months]);

  const arcColorMap = useMemo(
    () => new Map(arcs.map((a, i) => [a.label, ARC_PALETTE[i % ARC_PALETTE.length]])),
    [arcs]
  );

  // All commits flattened and grouped by arc — arcs are never split by month boundary.
  const allArcGroups = useMemo(() => groupByArc(months.flatMap((m) => m.commits)), [months]);

  const arcIds = useMemo(() => arcs.map((a) => arcId(a.label)), [arcs]);
  const activeArcId = useScrollSpy(arcIds);

  const [activeHash, setActiveHash] = useState(null);
  const activatePendingRef = useRef(null);
  const activateHash = useCallback((hash) => {
    clearTimeout(activatePendingRef.current);
    activatePendingRef.current = setTimeout(() => setActiveHash(hash), 80);
  }, []);

  const stats = useMemo(() => {
    const counts = {};
    months.forEach((month) => {
      month.commits.forEach((commit) => {
        counts[commit.type] = (counts[commit.type] ?? 0) + 1;
      });
    });
    return counts;
  }, [months]);

  const statTypes = Object.keys(TYPE_CONFIG).filter((t) => stats[t]);

  // Dot nav: floats below the legend bar, then sticks near the top of the
  // viewport once the user has scrolled the legend bar out of view.
  const legendRef = useRef(null);
  const legendDocBottomRef = useRef(0); // document-absolute bottom of legend
  const [dotsTop, setDotsTop] = useState(0);
  const [dotsLeft, setDotsLeft] = useState(8);

  const NAVBAR_H = 72; // height of the site navbar in px

  function computeDotsTop(docBottom) {
    // Legend viewport-relative bottom; positive → legend still visible.
    const viewportBottom = docBottom - window.scrollY;
    // When legend is visible: sit 12px below it.
    // When legend has scrolled off: lock to just below the navbar.
    return Math.max(NAVBAR_H + 8, viewportBottom + 12);
  }

  // Measure legend position at mount and on resize.
  useEffect(() => {
    function measure() {
      if (!legendRef.current) return;
      const rect = legendRef.current.getBoundingClientRect();
      const docBottom = Math.round(rect.bottom + window.scrollY);
      legendDocBottomRef.current = docBottom;
      setDotsLeft(Math.max(8, Math.round(rect.left)));
      setDotsTop(computeDotsTop(docBottom));
    }
    measure();
    window.addEventListener('resize', measure, { passive: true });
    return () => window.removeEventListener('resize', measure);
  }, [statTypes.length]);

  // Recompute top on every scroll tick.
  useEffect(() => {
    function onScroll() {
      if (!legendDocBottomRef.current) return;
      setDotsTop(computeDotsTop(legendDocBottomRef.current));
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <style>{PAGE_STYLES}</style>
      <div className="min-h-screen" style={{ background: '#f5f0e8', ...body }}>
        <header
          className="border-b-4 border-[#1a1a1a] px-6 py-10"
          style={{ background: '#ede5d4' }}
        >
          <div className="max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.25em] text-[#d62828] mb-2" style={mono}>
              VotePlan · Development History
            </div>
            <h1 className="text-6xl md:text-7xl text-[#1a1a1a] leading-none mb-3" style={display}>
              BUILD LOG
            </h1>
            <div className="flex flex-wrap items-center gap-4">
              <p className="text-lg text-[#1a1a1a]" style={body}>
                {totalCommits > 0
                  ? `${totalCommits} commits across ${months.length} month${months.length !== 1 ? 's' : ''}`
                  : 'Run npm run gen:timeline to populate'}
              </p>
              {generatedAt && (
                <span className="text-xs text-[#6b5f4d]" style={mono}>
                  Generated {generatedAt}
                </span>
              )}
            </div>
          </div>
        </header>

        {statTypes.length > 0 && (
          <div
            ref={legendRef}
            className="border-b-2 border-[#1a1a1a] px-6 py-3"
            style={{ background: '#fefcf6' }}
          >
            <div className="max-w-3xl mx-auto flex flex-wrap gap-2">
              {statTypes.map((type) => (
                <StatPill key={type} type={type} count={stats[type]} />
              ))}
            </div>
          </div>
        )}

        {/* Dot nav — floats below legend bar, then sticks near top when scrolled past */}
        {dotsTop > 0 && (
          <div
            style={{
              position: 'fixed',
              top: dotsTop,
              left: dotsLeft,
              zIndex: 9999,
            }}
          >
            <DotNav arcs={arcs} activeArcId={activeArcId} />
          </div>
        )}

        {/* Content row: spacer keeps commits offset from the floating dots */}
        <div className="max-w-3xl mx-auto px-2 py-8 flex gap-3">
          <div className="w-5 shrink-0" aria-hidden="true" />
          <main
            className="flex-1 min-w-0 pr-2 md:pr-4"
            onMouseLeave={() => {
              clearTimeout(activatePendingRef.current);
              setActiveHash(null);
            }}
          >
            {months.length === 0 ? (
              <EmptyState />
            ) : (
              <BubbleHoverCtx.Provider value={{ activeHash, setActiveHash: activateHash }}>
                <div className={`relative${activeHash ? '' : ' timeline-free'}`}>
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5"
                    style={{ background: '#c8bfae' }}
                  />
                  {allArcGroups.map((group, i) => (
                    <CommitGroup
                      key={group.arcKey ?? `ungrouped-${i}`}
                      group={group}
                      arcColor={group.arcKey ? arcColorMap.get(group.arcKey) : null}
                    />
                  ))}
                </div>
              </BubbleHoverCtx.Provider>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
